import { Copy, Link as LinkIcon, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

/**
 * Staff-side "Share Live Stay" panel (sub-spec 01d Plan 8 M8).
 *
 * Renders three states:
 *   - no token issued — shows "Generate link" button.
 *   - token issued — shows the public URL, a copy button, and a revoke button.
 *   - revoking / generating — disabled buttons with a spinner.
 *
 * The panel reads the current token from `GET /api/bookings/:id` (the booking
 * row's `share_token` column already surfaces via the bookings repo). To keep
 * this self-contained for v1 we fetch the bookings detail endpoint and parse
 * the share_token field; sub-spec 06 may surface a dedicated /share-token GET.
 *
 * The "share base" URL is the same hostname as the customer portal — we
 * derive it from `window.location` and substitute the customer subdomain.
 * Tenants on a custom domain see that domain naturally; tenants on the
 * default subdomain see `<slug>.kennel.app`.
 */

interface BookingShareState {
  token: string | null;
  status: 'idle' | 'loading' | 'busy' | 'error';
  error: string | null;
}

export interface ShareLiveStayPanelProps {
  bookingId: string;
  /**
   * Pre-fetched token if the caller already has it. When absent the panel
   * fetches the booking row to read the current value.
   */
  initialToken?: string | null;
  /** Customer-portal base URL. Defaults to `window.location.origin`. */
  portalBaseUrl?: string;
}

function liveStayUrl(token: string, portalBaseUrl: string): string {
  return `${portalBaseUrl.replace(/\/$/, '')}/stay/${encodeURIComponent(token)}`;
}

export function ShareLiveStayPanel(props: ShareLiveStayPanelProps) {
  const [state, setState] = useState<BookingShareState>({
    token: props.initialToken ?? null,
    status: 'idle',
    error: null,
  });
  const [copied, setCopied] = useState(false);
  const portalBase =
    props.portalBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');

  // If we weren't given an initial token, fetch it.
  useEffect(() => {
    if (props.initialToken !== undefined) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading' }));
    fetch(`/api/bookings/${encodeURIComponent(props.bookingId)}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`load failed (${res.status})`);
        return (await res.json()) as {
          booking?: { shareToken?: string | null; shareTokenRevokedAt?: string | null };
        };
      })
      .then((body) => {
        if (cancelled) return;
        const token =
          body.booking?.shareToken && !body.booking?.shareTokenRevokedAt
            ? body.booking.shareToken
            : null;
        setState({ token, status: 'idle', error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ token: null, status: 'idle', error: (err as Error).message });
      });
    return () => {
      cancelled = true;
    };
  }, [props.bookingId, props.initialToken]);

  const handleGenerate = useCallback(async () => {
    setState((s) => ({ ...s, status: 'busy', error: null }));
    try {
      const res = await fetch(
        `/api/bookings/${encodeURIComponent(props.bookingId)}/issue-share-token`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: '{}',
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `issue failed (${res.status})`);
      }
      const data = (await res.json()) as { token: string };
      setState({ token: data.token, status: 'idle', error: null });
    } catch (err) {
      setState((s) => ({ ...s, status: 'idle', error: (err as Error).message }));
    }
  }, [props.bookingId]);

  const handleRevoke = useCallback(async () => {
    setState((s) => ({ ...s, status: 'busy', error: null }));
    try {
      const res = await fetch(
        `/api/bookings/${encodeURIComponent(props.bookingId)}/revoke-share-token`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(`revoke failed (${res.status})`);
      setState({ token: null, status: 'idle', error: null });
    } catch (err) {
      setState((s) => ({ ...s, status: 'idle', error: (err as Error).message }));
    }
  }, [props.bookingId]);

  const handleCopy = useCallback(() => {
    if (!state.token) return;
    const url = liveStayUrl(state.token, portalBase);
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {
          /* swallow — UI fallback below */
        });
    }
  }, [state.token, portalBase]);

  const busy = state.status === 'busy' || state.status === 'loading';

  return (
    <div
      data-testid="share-live-stay-panel"
      className="rounded-lg border border-border bg-surface p-4"
    >
      <div className="mb-2 flex items-center gap-2">
        <LinkIcon size={14} className="text-muted-foreground" strokeWidth={1.75} />
        <h3 className="text-sm font-medium">Share live stay</h3>
      </div>
      {state.error && (
        <p
          role="alert"
          data-testid="share-error"
          className="mb-2 rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive"
        >
          {state.error}
        </p>
      )}
      {state.token ? (
        <>
          <p className="mb-2 text-xs text-muted-foreground">
            Anyone with this link can view photos, notes, and message you about this stay.
          </p>
          <div
            className="mb-3 flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2"
            data-testid="share-url-row"
          >
            <code
              data-testid="share-url"
              className="flex-1 truncate text-xs"
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            >
              {liveStayUrl(state.token, portalBase)}
            </code>
            <button
              type="button"
              data-testid="share-copy"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] hover:bg-secondary"
            >
              <Copy size={12} strokeWidth={1.75} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            type="button"
            data-testid="share-revoke"
            disabled={busy}
            onClick={() => void handleRevoke()}
            className="inline-flex items-center gap-1.5 rounded-md border border-destructive/30 bg-surface px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 size={12} className="animate-spin" strokeWidth={1.75} />
            ) : (
              <X size={12} strokeWidth={1.75} />
            )}
            Revoke link
          </button>
        </>
      ) : (
        <>
          <p className="mb-2 text-xs text-muted-foreground">
            Give the owner a sharable link to follow this stay in real time.
          </p>
          <button
            type="button"
            data-testid="share-generate"
            disabled={busy}
            onClick={() => void handleGenerate()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium hover:bg-secondary disabled:opacity-60"
          >
            {busy ? (
              <Loader2 size={12} className="animate-spin" strokeWidth={1.75} />
            ) : (
              <LinkIcon size={12} strokeWidth={1.75} />
            )}
            Generate link
          </button>
        </>
      )}
    </div>
  );
}
